import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  Chip,
  Grid,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Print as PrintIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  QrCode2 as QrCodeIcon,
  Receipt as ReceiptIcon,
  Mail as MailIcon,
  AccessTimeFilled as AccessTimeFilledIcon,
  AccountBalance as AccountBalanceIcon,
} from "@mui/icons-material";
import AttachEmailIcon from '@mui/icons-material/AttachEmail';
import SyncIcon from "@mui/icons-material/Sync";
import { makeStyles } from "@mui/styles";
import { createTheme } from "@mui/material/styles";
import MaterialTable, { MTableToolbar } from "material-table";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import {
  AddEmployeeCanteenRecordHelper,
  GetCanteenManagementRecordHelper,
  GetCanteenTokenBalanceHelper,
} from "../helpers/TokenConfigHelper";
import UnauthorizedBase from "../../Base/views/UnauthorizedBase";
import { ConvertDate, DateConvertInDbFormat, getImageSignedUrl } from "../../Base/helper/baseFunctions";
import { toast } from "react-toastify";
import TokenPreviewDialog from "../helpers/TokenPrintDialog";
import { EmailHelper } from "../../Ancillary/Email/helper/EmailHelper";
import { generateTokenContent, TokenPdf } from "../helpers/MailHelper";
import { renderEmail } from "react-html-email";
import { BaseEmailTemplate2 } from "../../Base/views/BaseEmailTemplate";
import CommonDatePicker from "../../Base/views/CommonDatePicker";
import { pdf } from "@react-pdf/renderer";
import BackdropMeepl from "../../Ancillary/AnimationComponents/BackdropMeepl";

const theme = createTheme({
  palette: {
    primary: { main: "#6C63FF" },
    secondary: { main: "#64748b" },
    meeplyellow: { main: "#fecb03" },
    background: {
      default: "transparent",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#000",
      secondary: "#475569",
    },
  },
});

const useStyles = makeStyles({
  root: {
    height: "calc(100vh - 75px)",
    // background: "linear-gradient(107.54deg, #F3F6FF 0.57%, #F2F6FF 46.35%, #DEE9FF 100%)",
    background: "#ffff",
    padding: "12px 32px",
    fontFamily: "'Inter', sans-serif",
    borderTop: "1px solid #e2e8f0",
    overflowY: "auto"
  },

  header: {
    marginBottom: "22px",
  },
  title: {
    color: "inherit",
    fontWeight: "700",
    fontSize: "24px",
    marginBottom: "4px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  subtitle: {
    color: "secondary",
    fontSize: "16px",
  },
  card: {
    // background: "linear-gradient(107.54deg, #F3F6FF 0.57%, #F2F6FF 46.35%, #DEE9FF 100%)",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    marginBottom: "24px",
    overflow: "hidden",
  },
  cardHeader: {
    padding: "20px 24px",
    borderBottom: "1px solid  #e2e8f0",
  },
  cardTitle: {
    color: theme?.palette?.meeplyellow?.main,
    fontWeight: "600",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  cardContent: {
    padding: "24px",
  },
  statusChip: {
    fontWeight: "600",
    fontSize: "12px",
  },
  activeChip: {
    backgroundColor: "#10b981",
    color: "#fff",
  },
  expiredChip: {
    backgroundColor: "#ef4444",
    color: "#fff",
  },
  usedChip: {
    backgroundColor: "#64748b",
    color: "#fff",
  },
  yellowbg: {
    background: "linear-gradient(135deg, #0f172a 0%, #111827 40%, #fecb03 100%)",
  },
  balanceCard: {
    background: "linear-gradient(135deg, #FECB03 0%, #F2B705 100%)",
    color: "inherit",
    padding: "20px",
    borderTopLeftRadius: "12px",
    borderTopRightRadius: "12px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  balanceAmount: {
    fontSize: "36px",
    fontWeight: "700",
  },
  balanceLabel: {
    fontSize: "14px",
    opacity: 0.9,
  },
  actionButton: {
    color: "#2dd4bf",
    "&:hover": {
      backgroundColor: "rgba(45, 212, 191, 0.1)",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: "48px 24px",
    color: "#64748b",
  },
  emptyIcon: {
    fontSize: "64px",
    marginBottom: "16px",
    opacity: 0.5,
  },
});
const generateButtonStyle = {
  backgroundColor: "#000",
  color: theme?.palette?.meeplyellow?.main,
  fontWeight: 600,
  textTransform: "none",
  fontSize: "16px",
  borderRadius: "8px",
  height: "40px",
  transition: "all 0.2s ease",

  "&:hover": {
    backgroundColor: "#111",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.4)",
    transform: "translateY(-2px)",
  },

  "&:active": {
    transform: "translateY(1px)",
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.3)",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(0, 0, 0, 0.8) !important",
    color: "#c7c3c3ff !important",
    cursor: "not-allowed !important",
    boxShadow: "none",
    transform: "none",
  },
};

const CanteenPortalPage = () => {
  const classes = useStyles();
  const currentDate = new Date();
  const [count, setCount] = useState(1);
  const [uuid, setUuid] = useState("");
  const [tokenData, setTokenData] = useState({});
  const [balanceLoading, setBalanceLoading] = useState(false);
  const { domain, userToken } = useParams();
  const decodedPayload = JSON.parse(atob(userToken));
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [generatedToken, setGeneratedToken] = useState(null);

  const formattedToday = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString()
    .padStart(2, "0")}-${currentDate.getDate().toString().padStart(2, "0")}`;
  const [imageUrl, setImageUrl] = useState("");

  const [filters, setFilters] = useState({
    employee_id: "",
    from_date: formattedToday,
    to_date: formattedToday,
    ats_work_location_id: [],
  });
  const handleFilter = (event, value) => {
    if (["from_date", "to_date"].includes(event?.target?.name)) {
      setFilters({ ...filters, [event?.target?.name]: event?.target?.value });
    }
  };
  useEffect(() => {
    const fetchImage = async () => {
      try {
        const url = await getImageSignedUrl(decodedPayload?.image);
        setImageUrl(url);
      } catch (error) {
        console.error("Error fetching signed URL:", error);
      }
    };

    if (decodedPayload?.image) {
      fetchImage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedPayload?.image]);

  const handleGenerate = (id) => {
    if (!id) return;
    AddEmployeeCanteenRecordHelper({
      domain_name: domain,
      uuid: id,
      token_amt: count,
    }).then((res) => {
      if (res?.data?.statuscode === 200) {
        const tokenObj = {
          ...tokenData,
          tokens: count,
          token_balance: +tokenData?.token_balance - +count,
          issued: formattedToday,
          company: decodedPayload?.companyFullName,
          region: decodedPayload?.region,
          token_id: res?.data?.data?.[0]?.fn_add_employee_canteen_record,
          domain_name: domain,
          today: formattedToday,
          user_id: decodedPayload?.user_id,
          canteen_management_regions_pk:
            decodedPayload?.canteen_management_regions_pk,
        };
        setGeneratedToken(tokenObj);
        setTokenDialogOpen(true);
        toast.dismiss();
        toast.success("Token generated successfully");
        fetchData();
        setTokenData({});
        setCount(1);
        setUuid("");
      } else {
        toast.dismiss();
        toast.error("Failed to generate token");
      }
    });
  };

  const handleTokenBalance = () => {
    setBalanceLoading(true);
    GetCanteenTokenBalanceHelper({ domain_name: domain, uuid: uuid }).then(
      (res) => {
        if (res?.data?.statuscode === 200) {
          const resData = res?.data?.data?.[0];
          if (resData) {
            setTokenData({...resData,
              ...( resData?.tab === "Visitor" && {
                employee_name: resData?.visitors_name,
                token_balance: resData?.token_balance >= 0 ? "1" : resData?.token_balance
              })
            });
          } else {
            toast.dismiss();
            toast.error("Invalid Token Id");
          }
        } else {
          toast.dismiss();
          toast.error("Something went wrong!")
        }
        setBalanceLoading(false);
      }
    );
  };
  const handlePrintToken = () => {
  //  no in use
  };
  const createTokenPdfAttachment = async () => {
    const blob = await pdf(
      <TokenPdf
        tokenCode={generatedToken?.uuid || ""}
        employeeName={generatedToken?.employee_name || ""}
        companyName={generatedToken?.company || ""}
        tokens={generatedToken?.tokens || "0"}
        region={generatedToken?.region || ""}
        imageUrl={imageUrl}
      />
    ).toBlob();

    const toBase64 = (blob) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

    const dataUrl = await toBase64(blob);
    const base64 = dataUrl.split(",")[1];

    const fileName = `${generatedToken?.region || "region"}-${
      generatedToken?.uuid || "token"
    }.pdf`;
    return {
      filename: fileName,
      content: base64,
      encoding: "base64",
      contentType: "application/pdf",
    };
  };

  const handleShareEmail = async (row, type) => {
    const rowData = {...row,
      company: decodedPayload?.companyFullName,
      region: decodedPayload?.region,
      tokens: row?.usage_tokens,
      issued: DateConvertInDbFormat(new Date(row?.usage_timestamp)),
    }
    const TokenData = type === "table" ? rowData : generatedToken
    if (!TokenData) return;
    const subject = `Meal Token - ${TokenData.uuid || ""}`;
    toast.info(type === "table" ? "Generating token.." : "Generating...");
    const emailContent = generateTokenContent(TokenData);
    const pdfAttachment = await createTokenPdfAttachment();
    toast.dismiss();
      await  EmailHelper({
        to: TokenData?.office_email,
        subject: subject,
        content: renderEmail(
                <BaseEmailTemplate2 company_logo={imageUrl}>
                  {emailContent}
                </BaseEmailTemplate2>
              ),
        attachments: [pdfAttachment],
        type: "token-mail",
        sendingMessage: "Sending token email.."
        }, "Token email sent successfully");
  };
  

  const downloadTokenPdf = async (generatedToken) => {
    try {
      toast.info("Generating...")
      const blob = await pdf(
        <TokenPdf
          tokenCode={generatedToken?.uuid || ""}
          employeeName={generatedToken?.employee_name || ""}
          companyName={decodedPayload?.companyFullName || ""}
          tokens={generatedToken?.usage_tokens || "0"}
          region={decodedPayload?.region || ""}
          imageUrl={imageUrl}
        />
      ).toBlob();

      const fileName = `${decodedPayload?.region}-${generatedToken?.uuid || "token"}.pdf`;

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(link.href);
      toast.dismiss();
    } catch (error) {
      toast.dismiss();
      console.error("PDF Download Error:", error);
    }
  };

  const [employeeRecordData, setEmployeeRecordData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const fetchData = () => {
    setDataLoading(true);
    GetCanteenManagementRecordHelper({
      domain_name: domain,
      from_date: filters?.from_date,
      to_date: filters?.to_date,
      user_id: decodedPayload?.user_id,
      canteen_management_regions_pk:
        decodedPayload?.canteen_management_regions_pk,
    }).then((res) => {
      if (res?.data?.statuscode === 200) {
        setEmployeeRecordData(res?.data?.data);
      }
      setDataLoading(false);
    });
  }
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  return (
    <UnauthorizedBase
      imageUrl={imageUrl}
      companyName={decodedPayload?.companyFullName}
    >
      <Box className={classes.root}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={9}>
            <Box className={classes.header}>
              <Typography className={classes.title}>
                <ReceiptIcon fontSize="large" />
                Token Printing {decodedPayload?.region ? `: ${decodedPayload?.region}` : ""}
              </Typography>
              <Typography className={classes.subtitle}>
                Generate, print and manage physical meal tokens
              </Typography>
             
            </Box>
            <Paper className={classes.card} sx={{ position: "relative" }}>
              <BackdropMeepl open={dataLoading} dark={false}/>
              {employeeRecordData.length > 0 ? (
                <MaterialTable
                  title={""}
                  columns={[
                    {
                      title: "Employee",
                      field: "employee_name",
                      render: (row) => (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <PersonIcon sx={{ color: "#475569" }} />
                          {row?.tab === "Visitor" ? row?.visitors_name : row?.employee_name}
                        </Box>
                      ),
                    },
                    {
                      title: "Email",
                      field: "office_email",
                      render: (row) => (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <MailIcon sx={{ color: "#475569" }} />
                          {row?.tab === "Visitor" ? "N/A" : row?.office_email}
                        </Box>
                      ),
                    },
                    {
                      title: "Token Count",
                      field: "usage_tokens",
                      render: (row) => (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <ReceiptIcon sx={{ color: "#475569" }} />
                          {row.usage_tokens}
                        </Box>
                      ),
                    },
                    {
                      title: "Generated On",
                      field: "usage_timestamp",
                      render: (row) => (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <AccessTimeFilledIcon sx={{ color: "#475569" }} />
                            <Typography variant="body2" fontWeight={500}>
                                {row?.usage_timestamp ? new Date(row?.usage_timestamp).toLocaleString("en-IN", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  second: "2-digit",
                                  hour12: true,
                              })
                              : "-"}
                            </Typography>
                        </Box>
                      ),
                    },
                    {
                      title: "Issued",
                      field: "issue_date",
                      render: (row) => (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CalendarIcon sx={{ color: "#475569" }} />
                          {ConvertDate(row.issue_date)}
                        </Box>
                      ),
                    },
                  ]}
                  data={employeeRecordData}
                  options={{
                    maxBodyHeight: "calc(100vh - 295px)",
                    minBodyHeight: "calc(100vh - 295px)",
                    rowStyle: { fontSize: "13px" },
                    headerStyle: {
                      backgroundColor:
                        theme?.palette?.mode === "light"
                          ? "#fff"
                          : theme.palette.grey[500],
                      color:
                        theme?.palette?.mode === "light"
                          ? "#000"
                          : theme.palette.grey[800],
                      fontWeight: "bold",
                      padding: "10px 10px 10px 13px",
                    },
                    sorting: false,
                    search: true,
                    searchText: "",
                    searchFieldAlignment: "right",
                    searchAutoFocus: false,
                    searchFieldVariant: "standard",
                    paging: true,
                    pageSizeOptions: [25, 50],
                    pageSize: 25,
                    paginationType: "stepped",
                    showFirstLastPageButtons: false,
                    paginationPosition: "bottom",
                    exportFileName: "",
                    exportAllData: true,
                    exportButton: false,
                    addRowPosition: "first",
                    selection: false,
                    actionsColumnIndex: -1,
                    grouping: false,
                    columnsButton: false,
                    toolbar: true,
                  }}
                  components={{
                    Toolbar: (props) => (
                      <Box sx={{ width: "full", display: "flex", flexDirection: { xs: "column", sm: "row" }, flexGrow: 1 }}>
                          <Box
                              sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              backgroundColor: "#f3edfd",
                              padding: "0px 0px 0px 20px",
                              flexGrow: 1,
                              minHeight: "48px !important",
                              }}
                          >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <QrCodeIcon color="primary" />
                            <Typography>Recent Token Prints</Typography>
                          </Box>
                          <Grid container spacing={1} xs={6} sx={{ display: "flex", alignItems: "center", pt: 0.5 }}>
                            <Grid
                              item
                              xs={12}
                              md={6}
                              sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            >
                              <FormControl sx={{ flex: 1, minWidth: 0 }}>
                                <CommonDatePicker
                                  name="from_date"
                                  label="From Date"
                                  size="small"
                                  pickerType="date"
                                  onKeyPress={(e) => e.preventDefault()}
                                  pickerFormat="dd/MM/yyyy"
                                  DefaultDTvalue={filters.from_date}
                                  maxDate={filters.to_date ? new Date(filters.to_date) : new Date()}
                                  handleChange={(value) =>
                                    handleFilter({
                                      target: {
                                        name: "from_date",
                                        value: value
                                          ? `${
                                              value?.getFullYear() +
                                              "-" +
                                              ("0" + (value?.getMonth() + 1)).slice(-2) +
                                              "-" +
                                              ("0" + value?.getDate()).slice(-2)
                                            }`
                                          : "",
                                      },
                                    })
                                  }
                                />
                              </FormControl>
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              md={6}
                              sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            >
                              <FormControl sx={{ flex: 1, minWidth: 0 }}>
                                <CommonDatePicker
                                  name="to_date"
                                  label="To Date"
                                  pickerType="date"
                                  size="small"
                                  onKeyPress={(e) => e.preventDefault()}
                                  pickerFormat="dd/MM/yyyy"
                                  DefaultDTvalue={filters.to_date}
                                  minDate={filters.from_date ? new Date(filters.from_date) : null}
                                  maxDate={currentDate}
                                  handleChange={(value) =>
                                    handleFilter({
                                      target: {
                                        name: "to_date",
                                        value: value
                                          ? `${
                                              value?.getFullYear() +
                                              "-" +
                                              ("0" + (value?.getMonth() + 1)).slice(-2) +
                                              "-" +
                                              ("0" + value?.getDate()).slice(-2)
                                            }`
                                          : "",
                                      },
                                    })
                                  }
                                />
                              </FormControl>
                            </Grid>
                          </Grid> 
                          </Box>
                              <Box
                              sx={{
                                  "& .MuiToolbar-root": {
                                  padding: "20px 16px 12px 16px !important",
                                  margin: "0 !important",
                                  minHeight: "48px !important",
                                  },
                                  "& .MuiFormControl-root": {
                                  margin: "0 !important",
                                  },
                                  "& .MuiInputBase-root": {
                                  marginLeft: "-40px !important",

                                  },
                                  backgroundColor: "#f3edfd",
                              }}
                              >
                                  <MTableToolbar {...props} />
                              </Box>
                          </Box>
                  ),
                }}
                  actions={[
                    {
                      icon: () => <PrintIcon color="primary" />,
                      tooltip: "Print Token",
                      onClick: (event, row) => downloadTokenPdf(row),
                    },
                    (rowData) => ({
                      icon: (props) => (
                        <AttachEmailIcon
                           sx={{ color: props?.disabled ? "#bdbdbd" : "#8b44ff" }}
                        />
                      ),
                      tooltip: (rowData?.tab === "Visitor" || !rowData.office_email) ? "" : "Share Token via Mail",
                      onClick: (event, row) => handleShareEmail(row, "table"),
                      disabled: rowData?.tab === "Visitor" || !rowData.office_email,
                    }),
                  ]}
                />
              ) : (
                <Box className={classes.emptyState}>
                  <ReceiptIcon className={classes.emptyIcon} />
                  <Typography variant="h6" gutterBottom>
                    No tokens generated yet
                  </Typography>
                  <Typography variant="body2">
                    Generate your first token to get started
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={3}>
            <Paper className={classes.card}>
              <Box className={`${classes.cardHeader} ${classes.yellowbg}`}>
                <PrintIcon sx={{ color: theme?.palette?.meeplyellow?.main }} />
                <Typography className={classes.cardTitle}>
                  Generate Tokens
                </Typography>
              </Box>
              <Box className={classes.cardContent} sx={{background: "#ffff"}}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <TextField
                        type="text"
                        value={uuid}
                        onChange={(e) => {
                          setUuid(e.target.value.toUpperCase());
                          setTokenData({});
                        }}
                        label="Token Id"
                        placeholder="MEEPL#122"
                        size="small"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Tooltip title={!uuid ? "Enter token id first " : ""}>
                      <span>
                        <Button
                          fullWidth
                          startIcon={
                            balanceLoading ? (
                              <CircularProgress
                                size={18}
                                disableShrink
                                sx={{ color: theme?.palette?.meeplyellow?.main, }}
                              />
                            ) : (
                              <SyncIcon />
                            )
                          }
                          onClick={handleTokenBalance}
                          sx={{ ...generateButtonStyle }}
                          disabled={!uuid || balanceLoading}
                        >
                          Get Token Balance
                        </Button>
                      </span>
                    </Tooltip>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <PersonIcon />
                    <Typography variant="body1" color={"#000"}>
                      Name : {tokenData?.employee_name || "-"}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: "-8px",
                    }}
                  >
                    <AccountBalanceIcon />
                    <Typography variant="body1" color={"#000"}>
                      Token Balance
                    </Typography>
                    <Chip
                      label={tokenData?.token_balance || "0"}
                      sx={{
                        ml: 1,
                        backgroundColor: theme?.palette?.meeplyellow?.main,
                        color: "#000",
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <TextField
                        type="number"
                        value={count}
                        onChange={(e) => setCount(e.target.value)}
                        InputProps={{ inputProps: { min: 1 } }}
                        label="Count"
                        size="small"
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Tooltip
                      title={
                        !tokenData?.employee_canteen_tokens_maping_id
                          ? "Get token balance first"
                          : tokenData?.token_balance < 1 ? "Token balance low"
                          : "" }
                    >
                      <span>
                        <Button
                          fullWidth
                          className={classes.generateButton}
                          startIcon={<PrintIcon />}
                          onClick={() =>
                            handleGenerate(
                              tokenData?.uuid
                            )
                          }
                          sx={{ ...generateButtonStyle }}
                          disabled={
                            !tokenData?.employee_canteen_tokens_maping_id || tokenData?.token_balance < 1
                          }
                        >
                          Generate
                        </Button>
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        <TokenPreviewDialog
          open={tokenDialogOpen}
          onClose={() => setTokenDialogOpen(false)}
          generatedToken={generatedToken}
          onPrint={handlePrintToken}
          onShareEmail={handleShareEmail}
          imageUrl={imageUrl}
        />
      </Box>
    </UnauthorizedBase>
  );
};

export default CanteenPortalPage;
